import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useRecoilValueLoadable } from 'recoil';
import { useHistory, useParams } from 'react-router-dom';
import { BlockUI } from 'primereact/blockui';
import { Button } from 'primereact/button';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { RadioButton } from 'primereact/radiobutton';
import {
  EARLY_SCRAP_ATTACHMENT_BLUEPRINT,
  EARLY_SCRAP_FORM_META,
  EARLY_SCRAP_INSPECTION_BLUEPRINT,
  EARLY_SCRAP_JUDGMENT_BLUEPRINT,
  EARLY_SCRAP_PHOTO_BLUEPRINT,
  EARLY_SCRAP_PHOTO_PART_CODES,
  buildEarlyScrapSubmitPayload,
  buildInspectorFromMyInfo,
  createEarlyScrapDefaultForm,
  isEarlyScrapConstructionMachinery,
  mapEarlyCheckToForm,
  mapMolitToVehicleInformation,
  mapMolitVehicleToEarlyScrap,
  resolveEarlyScrapPhotoSrc,
  splitOrgFromInspector,
} from '../../constants/EarlyScrapConstants';
import { PAYMENT_METHOD, YN } from '../../constants/Constants';
import {
  EarlyScrapConfirmationMethod,
  EarlyScrapHydraulicResult,
  EarlyScrapOperationalJudgment,
  EarlyScrapVehicleClass,
  EarlyScrapWorkingPartNoiseResult,
  EarlyScrapWorkingPartResult,
} from '../../enums/EarlyScrapEnums';
import { EarlyScrapService } from '../../services/EarlyScrapService';
import { ServiceProvider } from '../../services';
import { MyInfoUtil } from '../../utils/myInfoUtil';
import { myInfoSelector } from '../../recoil/selectors';
import _ from 'lodash';
import dayjs from 'dayjs';
import { useClaimPhotoSwipe } from '../../components/Accident/claimViewer/useClaimPhotoSwipe';
import './EarlyScrapRegistrationForm.css';
import EarlyScrapPrintDocument from './EarlyScrapPrintDocument';

const earlyScrapService = EarlyScrapService.getInstance();
const performanceCheckService = ServiceProvider.performanceCheck;

const normalizeCheckPriceItems = (items = []) =>
  (items || []).map((item) => ({
    ...item,
    id: String(item?.id ?? ''),
  }));
const associationService = ServiceProvider.association;
const priceService = ServiceProvider.price;
const molitService = ServiceProvider.molit;

const RadioField = ({ name, control, options, disabled = false }) => (
  <Controller
    name={name}
    control={control}
    render={({ field }) => (
      <div className="early-scrap-radio-group">
        {options.map((option) => (
          <div key={option.value} className="early-scrap-radio-item">
            <RadioButton
              inputId={`${name}-${option.value}`}
              name={field.name}
              value={option.value}
              onChange={(e) => field.onChange(e.value)}
              checked={field.value === option.value}
              disabled={disabled}
            />
            <label htmlFor={`${name}-${option.value}`}>{option.label}</label>
          </div>
        ))}
      </div>
    )}
  />
);

const TextField = ({
  name,
  control,
  placeholder,
  className = 'w-full',
  disabled = false,
}) => (
  <Controller
    name={name}
    control={control}
    render={({ field }) => (
      <InputText
        {...field}
        className={className}
        placeholder={placeholder}
        disabled={disabled}
      />
    )}
  />
);

const CheckItem = ({
  name,
  control,
  item,
  disabled = false,
  compact = false,
}) => (
  <div className="es-check-row">
    <div className="es-check-label">
      {compact ? (
        <strong>{item.description}</strong>
      ) : (
        <>
          <strong>{item.title}</strong>
          <span>{item.description}</span>
        </>
      )}
    </div>
    <RadioField
      name={name}
      control={control}
      options={item.valueItems}
      disabled={disabled}
    />
  </div>
);

/** 직인 JPG/흰 배경을 투명 PNG로 변환 (이미 투명한 픽셀은 유지) */
const knockoutSealBackground = (dataUrl) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const px = imageData.data;
        for (let i = 0; i < px.length; i += 4) {
          if (px[i + 3] === 0) continue;
          const ink = 255 - Math.min(px[i], px[i + 1], px[i + 2]);
          px[i + 3] = Math.round((px[i + 3] * ink) / 255);
        }
        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (error) {
        resolve(dataUrl);
      }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });

const EARLY_SCRAP_PRINT_STYLE_ID = 'early-scrap-print-page-style';

const applyEarlyScrapPrintPageStyle = () => {
  document.body.classList.add('early-scrap-printing');
  if (!document.getElementById(EARLY_SCRAP_PRINT_STYLE_ID)) {
    const styleEl = document.createElement('style');
    styleEl.id = EARLY_SCRAP_PRINT_STYLE_ID;
    styleEl.textContent = '@page { size: A4 portrait; margin: 0; }';
    document.head.appendChild(styleEl);
  }
};

const clearEarlyScrapPrintPageStyle = () => {
  document.body.classList.remove('early-scrap-printing');
  document.getElementById(EARLY_SCRAP_PRINT_STYLE_ID)?.remove();
};

const EarlyScrapRegistrationForm = () => {
  const history = useHistory();
  const { dataId: dataIdParam } = useParams();
  const defaultValues = useMemo(() => createEarlyScrapDefaultForm(), []);
  const { control, handleSubmit, setValue, getValues, reset } = useForm({
    defaultValues,
  });
  const [loading, setLoading] = useState(false);
  const [carInformationLoading, setCarInformationLoading] = useState(false);
  const [photoPreviews, setPhotoPreviews] = useState({});
  const [photoFiles, setPhotoFiles] = useState({});
  const [existingPhotos, setExistingPhotos] = useState({});
  const [checkPriceTable, setCheckPriceTable] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [associationSealUrl, setAssociationSealUrl] = useState('');
  const { openGallery } = useClaimPhotoSwipe();

  // 조기폐차 인쇄 중에만 portrait @page 적용 (전역 CSS는 성능지 landscape를 깨뜨림)
  useEffect(() => {
    window.addEventListener('beforeprint', applyEarlyScrapPrintPageStyle);
    window.addEventListener('afterprint', clearEarlyScrapPrintPageStyle);
    return () => {
      clearEarlyScrapPrintPageStyle();
      window.removeEventListener('beforeprint', applyEarlyScrapPrintPageStyle);
      window.removeEventListener('afterprint', clearEarlyScrapPrintPageStyle);
    };
  }, []);

  const myInfoLoadable = useRecoilValueLoadable(myInfoSelector);
  const myInfo = useMemo(
    () => MyInfoUtil.unpack(myInfoLoadable.contents),
    [myInfoLoadable.contents]
  );

  const vehicleClass = useWatch({ control, name: 'vehicle.vehicleClass' });
  const confirmationMethod = useWatch({
    control,
    name: 'confirmationMethod',
  });
  const checkPrice = useWatch({
    control,
    name: 'paymentInformation.checkPrice',
  });
  const checkPriceVAT = useWatch({
    control,
    name: 'paymentInformation.checkPriceVAT',
  });
  const checkPriceItemId = useWatch({
    control,
    name: 'paymentInformation.checkPriceItemId',
  });
  const discountedCheckPrice = useWatch({
    control,
    name: 'paymentInformation.discountedCheckPrice',
  });
  const formAssociationId = useWatch({
    control,
    name: 'association.associationId',
  });
  const isConstructionMachinery =
    isEarlyScrapConstructionMachinery(vehicleClass);
  const isOnlineConfirmation =
    confirmationMethod === EarlyScrapConfirmationMethod.ONLINE;
  const formValues = useWatch({ control });
  const isEditMode = Boolean(dataIdParam);
  const checkPriceOptions = useMemo(() => {
    const items = normalizeCheckPriceItems(checkPriceTable?.checkPriceItems);
    const savedId = String(checkPriceItemId || '');
    if (!savedId || items.some((item) => item.id === savedId)) {
      return items;
    }
    const total =
      (_.toNumber(checkPrice) || 0) + (_.toNumber(checkPriceVAT) || 0);
    return [
      {
        id: savedId,
        title:
          total > 0
            ? `기존 점검료 (${total.toLocaleString()}원)`
            : '기존 점검료',
        price: total,
      },
      ...items,
    ];
  }, [checkPriceTable, checkPriceItemId, checkPrice, checkPriceVAT]);

  const applyCheckPrice = ({
    table = checkPriceTable,
    selectedItemId = getValues('paymentInformation.checkPriceItemId'),
    discount = getValues('paymentInformation.discountedCheckPrice'),
  } = {}) => {
    const { checkPriceTableId, checkPriceItems } = table || {};
    const items = normalizeCheckPriceItems(checkPriceItems);
    if (!items.length) {
      if (isEditMode) return;
      setValue('paymentInformation.checkPriceTableId', checkPriceTableId || 0);
      setValue('paymentInformation.checkPriceItemId', '');
      setValue('paymentInformation.checkPrice', 0);
      setValue('paymentInformation.checkPriceVAT', 0);
      return;
    }

    const itemId = String(selectedItemId || '');
    const item =
      items.find((row) => row.id === itemId) || (!itemId ? items[0] : null);
    if (!item) {
      const currentTotal =
        (_.toNumber(getValues('paymentInformation.checkPrice')) || 0) +
        (_.toNumber(getValues('paymentInformation.checkPriceVAT')) || 0);
      const discountAmount = _.toNumber(discount) || 0;
      if (currentTotal - discountAmount <= 0) {
        setValue(
          'paymentInformation.checkPricePaidAt',
          dayjs().format('YYYY-MM-DDTHH:mm:ss')
        );
        setValue('paymentInformation.checkPricePaidYN', YN.YES);
      } else {
        setValue('paymentInformation.checkPricePaidAt', null);
        setValue('paymentInformation.checkPricePaidYN', YN.NO);
      }
      return;
    }
    const total = _.toNumber(_.get(item, 'price')) || 0;
    const vat = Math.floor(total / 11 / 10) * 10;
    const price = total - vat;
    const discountAmount = _.toNumber(discount) || 0;

    setValue('paymentInformation.checkPriceTableId', checkPriceTableId || 0);
    setValue('paymentInformation.checkPriceItemId', item?.id ?? '');
    setValue('paymentInformation.checkPrice', price);
    setValue('paymentInformation.checkPriceVAT', vat);

    if (total - discountAmount <= 0) {
      setValue(
        'paymentInformation.checkPricePaidAt',
        dayjs().format('YYYY-MM-DDTHH:mm:ss')
      );
      setValue('paymentInformation.checkPricePaidYN', YN.YES);
    } else {
      setValue('paymentInformation.checkPricePaidAt', null);
      setValue('paymentInformation.checkPricePaidYN', YN.NO);
    }
  };

  useEffect(() => {
    if (!isConstructionMachinery) {
      setValue('vehicle.heavyEquipmentSpec', '');
      setValue('inspectionResults.hydraulic', null);
      setValue('inspectionResults.workingPartOperation', null);
      setValue('inspectionResults.workingPartNoise', null);
      return;
    }
    if (!getValues('inspectionResults.hydraulic')) {
      setValue(
        'inspectionResults.hydraulic',
        EarlyScrapHydraulicResult.GOOD
      );
    }
    if (!getValues('inspectionResults.workingPartOperation')) {
      setValue(
        'inspectionResults.workingPartOperation',
        EarlyScrapWorkingPartResult.GOOD
      );
    }
    if (!getValues('inspectionResults.workingPartNoise')) {
      setValue(
        'inspectionResults.workingPartNoise',
        EarlyScrapWorkingPartNoiseResult.GOOD
      );
    }
  }, [isConstructionMachinery, setValue, getValues]);

  useEffect(() => {
    if (!isOnlineConfirmation) return;
    setPhotoPreviews({});
    setPhotoFiles({});
  }, [isOnlineConfirmation]);

  useEffect(() => {
    const associationId =
      formAssociationId || myInfo.myAssociationId || 0;
    if (!associationId) return;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await associationService.getDataMobile(associationId);
        const path = data?.officialSealPath;
        if (!path || cancelled) return;
        const url = _.startsWith(path, 'http')
          ? path
          : `${process.env.REACT_APP_S3_BASE_URL || ''}${path}`;
        const { data: encodedUrl } =
          await performanceCheckService.getBase64Url(url);
        if (cancelled || !encodedUrl) return;
        const rawUrl = `data:image/png;base64, ${encodedUrl}`;
        const transparentUrl = await knockoutSealBackground(rawUrl);
        if (cancelled) return;
        setAssociationSealUrl(transparentUrl);
      } catch (error) {
        console.error('조기폐차 협회 직인 로드 실패', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [formAssociationId, myInfo.myAssociationId]);

  const applyInspector = (inspectorPayload) => {
    const org = splitOrgFromInspector(inspectorPayload);
    setValue('inspectorPayload', inspectorPayload);
    setValue('association', org.association);
    setValue('enterprise', org.enterprise);
    setValue('shop', org.shop);
    setValue('inspector.shopName', inspectorPayload.shopName || '');
    setValue('inspector.inspectorName', inspectorPayload.userName || '');
    if (!getValues('inspector.checkDate')) {
      setValue('inspector.checkDate', dayjs().format('YYYY-MM-DD'));
    }
  };

  useEffect(() => {
    if (myInfoLoadable.state !== 'hasValue') return;

    const { myAssociationId, myEnterpriseId, myShopId } = myInfo;
    const myInfoRaw = myInfoLoadable.contents;

    if (!dataIdParam && !getValues('inspectorPayload.userId') && myInfoRaw) {
      applyInspector(buildInspectorFromMyInfo(myInfoRaw));
    }

    (async () => {
      if (!myShopId && !myEnterpriseId) return;

      try {
        const { data } = await priceService.getCheckPrice({
          associationId: myAssociationId || 0,
          enterpriseId: myEnterpriseId || 0,
          shopId: myShopId || 0,
        });
        setCheckPriceTable(data || null);
        if (!dataIdParam) {
          applyCheckPrice({
            table: data,
            selectedItemId: _.get(data, 'checkPriceItems.0.id'),
            discount: 0,
          });
        }
      } catch (error) {
        console.error('조기폐차 점검료 조회 실패', error);
        setCheckPriceTable(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    myInfoLoadable.state,
    myInfo.myAssociationId,
    myInfo.myEnterpriseId,
    myInfo.myShopId,
    dataIdParam,
  ]);

  useEffect(() => {
    if (!dataIdParam) return;

    (async () => {
      setLoading(true);
      try {
        const { data } = await performanceCheckService.getData(dataIdParam);
        const { performanceCheckData, performanceCheckPhotos } = data || {};
        if (!performanceCheckData) {
          throw { code: 'EMPTY', message: '조회된 조기폐차 확인서가 없습니다.' };
        }

        reset({
          ...createEarlyScrapDefaultForm(),
          ...mapEarlyCheckToForm(performanceCheckData),
        });

        const photos = performanceCheckPhotos || [];
        const nextPreviews = {};
        const nextExisting = {};
        photos.forEach((photo) => {
          const position = Object.keys(EARLY_SCRAP_PHOTO_PART_CODES).find(
            (key) => EARLY_SCRAP_PHOTO_PART_CODES[key] === photo.partCode
          );
          if (!position) return;
          const src = resolveEarlyScrapPhotoSrc(photo.filePath);
          nextPreviews[position] = src;
          nextExisting[position] = {
            photoId: photo.photoId,
            partCode: photo.partCode,
          };
        });
        setPhotoPreviews(nextPreviews);
        setExistingPhotos(nextExisting);
        setPhotoFiles({});
      } catch (error) {
        window.cerp.dialog.error(
          '조기폐차 확인서 조회 실패',
          `[${error?.code || 'ERROR'}] ${error?.message || '확인서를 불러오지 못했습니다.'}`
        );
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataIdParam]);

  const handlePhotoChange = (position, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setPhotoPreviews((prev) => ({ ...prev, [position]: previewUrl }));
    setPhotoFiles((prev) => ({ ...prev, [position]: file }));
  };

  const openPhotoViewer = (position) => {
    const available = EARLY_SCRAP_PHOTO_BLUEPRINT.positions.filter(
      ({ position: key }) => photoPreviews[key]
    );
    const slides = available.map(({ position: key, htmlLabel }) => ({
      src: photoPreviews[key],
      alt: htmlLabel,
    }));
    const index = available.findIndex((item) => item.position === position);
    if (index < 0 || !slides.length) return;
    openGallery(slides, index);
  };

  const getCarInformation = async (event) => {
    event?.preventDefault?.();

    if (isConstructionMachinery) {
      window.cerp.dialog.warn(
        '차량조회',
        '건설기계는 국토부 조회 대상이 아닙니다. 명세를 수기로 입력해 주세요.'
      );
      return;
    }

    const licensePlateNumber = (
      getValues('vehicle.licensePlateNumber') || ''
    ).trim();

    if (!licensePlateNumber) {
      window.cerp.dialog.error(
        '차량조회 실패',
        '[EMPTY] 차량 번호를 입력하세요.'
      );
      return;
    }

    setCarInformationLoading(true);
    setLoading(true);
    try {
      const {
        data: {
          molitData: {
            RECORD1: [rawVehicleInformation],
          },
        },
      } = await molitService.vehicleInformation([licensePlateNumber]);

      if (!rawVehicleInformation) {
        throw { code: 'EMPTY', message: '조회된 차량정보가 없습니다.' };
      }

      const mapped = mapMolitVehicleToEarlyScrap(rawVehicleInformation);
      Object.entries(mapped).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          setValue(`vehicle.${key}`, value);
        }
      });
      setValue('vehicle.vehicleClass', EarlyScrapVehicleClass.AUTOMOBILE);
      setValue(
        'vehicleInformation',
        mapMolitToVehicleInformation(
          rawVehicleInformation,
          getValues('vehicleInformation')
        )
      );

      window.cerp.toast.success(
        '차량정보조회',
        '국토부 TS 차량정보를 명세에 반영했습니다.'
      );
    } catch (error) {
      window.cerp.dialog.error(
        '차량조회 실패',
        `[${error?.code || 'ERROR'}] ${error?.message || '차량정보 조회에 실패했습니다.'}`
      );
    } finally {
      setCarInformationLoading(false);
      setLoading(false);
    }
  };

  const validateSubmit = (formData) => {
    if (!formData.vehicle?.licensePlateNumber?.trim()) {
      return '차량번호를 입력하세요.';
    }
    if (!formData.owner?.name?.trim()) {
      return '소유자 성명을 입력하세요.';
    }
    if (!formData.owner?.phone?.trim()) {
      return '소유자 휴대전화를 입력하세요.';
    }
    if (!formData.inspectorPayload?.userId) {
      return '로그인 사용자 정보를 불러오지 못했습니다. 다시 로그인해 주세요.';
    }
    if (
      formData.judgment?.operationalStatus ===
        EarlyScrapOperationalJudgment.FAIL &&
      !formData.judgment?.failReason?.trim()
    ) {
      return '불가 판정 시 불가 사유를 입력하세요.';
    }
    if (
      isEarlyScrapConstructionMachinery(formData.vehicle?.vehicleClass) &&
      (!formData.inspectionResults?.hydraulic ||
        !formData.inspectionResults?.workingPartOperation ||
        !formData.inspectionResults?.workingPartNoise)
    ) {
      return '건설기계는 유압장치 확인 결과를 모두 선택하세요.';
    }
    if (
      checkPriceOptions.length &&
      !String(formData.paymentInformation?.checkPriceItemId || '').trim()
    ) {
      return '점검료구분을 선택하세요.';
    }
    if (
      (_.toNumber(formData.paymentInformation?.discountedCheckPrice) || 0) >
        0 &&
      !formData.paymentInformation?.discountReason?.trim()
    ) {
      return '할인 사유를 입력하세요.';
    }
    if (
      formData.confirmationMethod === EarlyScrapConfirmationMethod.ONSITE
    ) {
      const uploaded = EARLY_SCRAP_PHOTO_BLUEPRINT.positions.filter(
        ({ position }) => photoFiles[position] || photoPreviews[position]
      ).length;
      if (uploaded < EARLY_SCRAP_PHOTO_BLUEPRINT.requiredCount) {
        return '현장 확인은 전·후·좌·우면 사진 4매가 필요합니다.';
      }
    }
    return null;
  };

  const onSubmit = async (formData) => {
    const validationMessage = validateSubmit(formData);
    if (validationMessage) {
      window.cerp.dialog.warn('입력 확인', validationMessage);
      return;
    }

    setLoading(true);
    window.cerp.toast.showStickyToast(
      isEditMode
        ? '조기폐차 확인서를 수정하고 있습니다.'
        : '조기폐차 확인서를 저장하고 있습니다.'
    );

    try {
      const { payload, files } = buildEarlyScrapSubmitPayload(
        formData,
        photoFiles,
        'C',
        { dataId: dataIdParam, existingPhotos }
      );
      const { data } = isEditMode
        ? await earlyScrapService.update(payload, files)
        : await earlyScrapService.register(payload, files);
      const saved = data?.performanceCheckData || {};
      const statementNumber =
        saved.statementNumber ||
        payload.licensePlateNumber ||
        getValues('inspectNumber');
      const savedDataId = saved.dataId || dataIdParam;

      if (statementNumber) {
        setValue('inspectNumber', statementNumber);
      }
      setPhotoFiles({});

      window.cerp.toast.success(
        isEditMode ? '조기폐차 확인서 수정' : '조기폐차 확인서 저장',
        isEditMode
          ? `점검번호 [${statementNumber}]가 수정되었습니다.`
          : `점검번호 [${statementNumber}]로 저장되었습니다.`
      );

      if (!isEditMode && statementNumber && savedDataId) {
        history.replace(
          `/performance-check/early-scrap/form/${statementNumber}-${savedDataId}`
        );
      }

      if (window.opener?.getListByChildren) {
        window.opener.getListByChildren();
      }
    } catch (error) {
      window.cerp.dialog.error(
        isEditMode ? '조기폐차 확인서 수정 실패' : '조기폐차 확인서 저장 실패',
        `[${error?.code || 'ERROR'}] ${
          error?.message ||
          (isEditMode
            ? '수정에 실패했습니다.'
            : '저장에 실패했습니다.')
        }`
      );
    } finally {
      window.cerp.toast.clear();
      setLoading(false);
    }
  };

  const closeAfterDelete = () => {
    if (window.opener?.getListByChildren) {
      window.opener.getListByChildren();
    }
    if (window.opener) {
      window.close();
      return;
    }
    history.push('/performance-check/list');
  };

  const handleDelete = async () => {
    if (!dataIdParam) return;

    setLoading(true);
    try {
      const { data } = await earlyScrapService.delete(dataIdParam);
      const inspectNumber =
        data?.statementNumber || getValues('inspectNumber') || dataIdParam;
      window.cerp.toast.success(
        '조기폐차 삭제 완료',
        `점검번호 [${inspectNumber}]가 삭제되었습니다.`
      );
      closeAfterDelete();
    } catch (error) {
      window.cerp.dialog.error(
        '조기폐차 삭제 실패',
        `[${error?.code || 'ERROR'}] ${
          error?.message || '확인서를 삭제하지 못했습니다.'
        }`
      );
    } finally {
      setShowDeleteConfirm(false);
      setLoading(false);
    }
  };

  return (
    <BlockUI blocked={loading} fullScreen>
      <div className="early-scrap-page">
        <div className="early-scrap-container early-scrap-screen-only early-scrap-compact">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="early-scrap-header">
              <div className="early-scrap-form-type">
                [{EARLY_SCRAP_FORM_META.formType}]
              </div>
              <h1>{EARLY_SCRAP_FORM_META.title}</h1>
              <div className="early-scrap-doc-meta">
                <label htmlFor="inspectNumber">점검번호</label>
                <Controller
                  name="inspectNumber"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="inspectNumber"
                      {...field}
                      readOnly
                      className="early-scrap-inspect-number"
                      placeholder="저장 시 자동채번"
                      title="저장 시 연월일(YYYYMMDD) + 4자리 순번으로 채번됩니다."
                    />
                  )}
                />
              </div>
            </div>

            <div className="es-split">
              <section className="es-section">
                <div className="es-section-title">소유자</div>
                <div className="es-section-body">
                  <div className="es-fields es-fields-2">
                    <label className="es-field">
                      <span>성명</span>
                      <TextField name="owner.name" control={control} />
                    </label>
                    <label className="es-field">
                      <span>생년월일</span>
                      <TextField
                        name="owner.birthDate"
                        control={control}
                        placeholder="6자리"
                      />
                    </label>
                    <label className="es-field es-span-2">
                      <span>주소</span>
                      <TextField name="owner.address" control={control} />
                    </label>
                    <label className="es-field">
                      <span>휴대전화 (차주 본인)</span>
                      <TextField
                        name="owner.phone"
                        control={control}
                        placeholder="010-0000-0000"
                      />
                    </label>
                    <label className="es-field">
                      <span>FAX</span>
                      <TextField name="owner.fax" control={control} />
                    </label>
                  </div>
                </div>
              </section>

              <section className="es-section">
                <div className="es-section-title">점검료</div>
                <div className="es-section-body">
                  <div className="es-fields es-fields-2">
                    <label className="es-field">
                      <span>점검료구분</span>
                      <Controller
                        name="paymentInformation.checkPriceItemId"
                        control={control}
                        render={({ field }) => (
                          <Dropdown
                            {...field}
                            value={String(field.value || '')}
                            className="w-full"
                            options={checkPriceOptions}
                            optionLabel="title"
                            optionValue="id"
                            placeholder={
                              checkPriceOptions.length ? '선택' : '점검료 없음'
                            }
                            disabled={!checkPriceOptions.length}
                            onChange={(e) => {
                              const nextId = String(e.value || '');
                              field.onChange(nextId);
                              applyCheckPrice({ selectedItemId: nextId });
                            }}
                          />
                        )}
                      />
                    </label>
                    <label className="es-field">
                      <span>점검료 (VAT포함)</span>
                      <InputNumber
                        className="w-full"
                        value={
                          (_.toNumber(checkPrice) || 0) +
                          (_.toNumber(checkPriceVAT) || 0)
                        }
                        suffix=" 원"
                        readOnly
                      />
                    </label>
                    <label className="es-field">
                      <span>결제</span>
                      <Controller
                        name="paymentInformation.checkPricePaidBy"
                        control={control}
                        render={({ field }) => (
                          <Dropdown
                            {...field}
                            className="w-full"
                            options={PAYMENT_METHOD}
                            onChange={(e) => field.onChange(e.value)}
                          />
                        )}
                      />
                    </label>
                    <label className="es-field">
                      <span>할인 금액</span>
                      <Controller
                        name="paymentInformation.discountedCheckPrice"
                        control={control}
                        render={({ field }) => (
                          <InputNumber
                            className="w-full"
                            value={field.value}
                            suffix=" 원"
                            onFocus={(e) => e.target.select()}
                            onValueChange={(e) => {
                              const value = e.value || 0;
                              field.onChange(value);
                              applyCheckPrice({ discount: value });
                            }}
                          />
                        )}
                      />
                    </label>
                    <label className="es-field es-span-2">
                      <span>할인사유</span>
                      <Controller
                        name="paymentInformation.discountReason"
                        control={control}
                        render={({ field }) => (
                          <InputText
                            {...field}
                            className="w-full"
                            placeholder={
                              (_.toNumber(discountedCheckPrice) || 0) > 0
                                ? '할인 사유를 입력하세요.'
                                : ''
                            }
                          />
                        )}
                      />
                    </label>
                  </div>
                </div>
              </section>
            </div>

            <section className="es-section">
              <div className="es-section-title">신청 자동차 명세</div>
              <div className="es-section-body">
                <div className="es-fields">
                  <div className="es-field es-span-2">
                    <span>구분</span>
                    <Controller
                      name="vehicle.vehicleClass"
                      control={control}
                      render={({ field }) => (
                        <div className="early-scrap-radio-group">
                          <div className="early-scrap-radio-item">
                            <RadioButton
                              inputId="vehicle-automobile"
                              value={EarlyScrapVehicleClass.AUTOMOBILE}
                              onChange={(e) => field.onChange(e.value)}
                              checked={
                                field.value === EarlyScrapVehicleClass.AUTOMOBILE
                              }
                            />
                            <label htmlFor="vehicle-automobile">
                              자동차 (국토부 조회)
                            </label>
                          </div>
                          <div className="early-scrap-radio-item">
                            <RadioButton
                              inputId="vehicle-construction"
                              value={
                                EarlyScrapVehicleClass.CONSTRUCTION_MACHINERY
                              }
                              onChange={(e) => field.onChange(e.value)}
                              checked={
                                field.value ===
                                EarlyScrapVehicleClass.CONSTRUCTION_MACHINERY
                              }
                            />
                            <label htmlFor="vehicle-construction">
                              건설기계 (수기)
                            </label>
                          </div>
                        </div>
                      )}
                    />
                  </div>
                  <label className="es-field es-span-2">
                    <span>차량번호</span>
                    <div className="early-scrap-plate-lookup">
                      <Controller
                        name="vehicle.licensePlateNumber"
                        control={control}
                        render={({ field }) => (
                          <InputText
                            {...field}
                            className="w-full"
                            placeholder={
                              isConstructionMachinery
                                ? '건설기계 번호 수기 입력'
                                : '차량번호 입력 후 조회'
                            }
                            onChange={(e) => {
                              const value = e.target.value.replace(
                                /[^ㄱ-힣0-9ㆍᆢㆍ‥ㆍ ᆢㆍ ᆢ]+/g,
                                ''
                              );
                              field.onChange(value);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !isConstructionMachinery) {
                                getCarInformation(e);
                              }
                            }}
                          />
                        )}
                      />
                      <Button
                        type="button"
                        label="조회"
                        icon="pi pi-search"
                        className="p-button-sm"
                        loading={carInformationLoading}
                        disabled={isConstructionMachinery}
                        title={
                          isConstructionMachinery
                            ? '건설기계는 수기 입력합니다.'
                            : '국토부 TS 차량정보 조회'
                        }
                        onClick={getCarInformation}
                      />
                    </div>
                  </label>
                  <label className="es-field">
                    <span>차대번호</span>
                    <TextField
                      name="vehicle.vin"
                      control={control}
                      placeholder={isConstructionMachinery ? '수기 입력' : ''}
                    />
                  </label>
                  <label className="es-field">
                    <span>차량명 (건설기계명)</span>
                    <TextField name="vehicle.vehicleName" control={control} />
                  </label>
                  <label className="es-field">
                    <span>연식</span>
                    <div className="early-scrap-inline">
                      <Controller
                        name="vehicle.modelYear"
                        control={control}
                        render={({ field }) => (
                          <InputNumber
                            className="w-full"
                            value={field.value}
                            onValueChange={(e) => field.onChange(e.value)}
                            useGrouping={false}
                          />
                        )}
                      />
                      <span>년</span>
                    </div>
                  </label>
                  <label className="es-field">
                    <span>배기량</span>
                    <div className="early-scrap-inline">
                      <Controller
                        name="vehicle.displacement"
                        control={control}
                        render={({ field }) => (
                          <InputNumber
                            className="w-full"
                            value={field.value}
                            onValueChange={(e) => field.onChange(e.value)}
                            useGrouping={false}
                          />
                        )}
                      />
                      <span>CC</span>
                    </div>
                  </label>
                  <label className="es-field">
                    <span>정원(승합)</span>
                    <div className="early-scrap-inline">
                      <Controller
                        name="vehicle.seatingCapacity"
                        control={control}
                        render={({ field }) => (
                          <InputNumber
                            className="w-full"
                            value={field.value}
                            onValueChange={(e) => field.onChange(e.value)}
                            useGrouping={false}
                          />
                        )}
                      />
                      <span>명</span>
                    </div>
                  </label>
                  <label className="es-field">
                    <span>규격 (건설기계)</span>
                    <TextField
                      name="vehicle.heavyEquipmentSpec"
                      control={control}
                      placeholder="건설기계 선택시 작성"
                      disabled={!isConstructionMachinery}
                    />
                  </label>
                  <div className="es-field es-span-2">
                    <span>중량(화물)</span>
                    <div className="early-scrap-inline">
                      <span>적재중량</span>
                      <TextField
                        name="vehicle.loadWeight"
                        control={control}
                        className="early-scrap-weight-input"
                      />
                      <span>톤 / 총중량</span>
                      <TextField
                        name="vehicle.totalWeight"
                        control={control}
                        className="early-scrap-weight-input"
                      />
                      <span>톤</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="es-section">
              <div className="es-section-title">확인 결과</div>
              <div className="es-section-body">
                <div className="es-checks">
                  {[
                    'identity',
                    'engine',
                    'powerTransmission',
                    'steering',
                    'brake',
                  ].map((key) => (
                    <CheckItem
                      key={key}
                      name={`inspectionResults.${key}`}
                      control={control}
                      item={EARLY_SCRAP_INSPECTION_BLUEPRINT[key]}
                    />
                  ))}
                  <div className="es-group-title">그외 주요 부품</div>
                  {[
                    'exterior',
                    'bumper',
                    'glass',
                    'tire',
                    'lighting',
                  ].map((key) => (
                    <CheckItem
                      key={key}
                      name={`inspectionResults.${key}`}
                      control={control}
                      item={EARLY_SCRAP_INSPECTION_BLUEPRINT[key]}
                      compact
                    />
                  ))}
                  {isConstructionMachinery && (
                    <>
                      <div className="es-group-title">유압장치 (건설기계)</div>
                      {[
                        'hydraulic',
                        'workingPartOperation',
                        'workingPartNoise',
                      ].map((key) => (
                        <CheckItem
                          key={key}
                          name={`inspectionResults.${key}`}
                          control={control}
                          item={EARLY_SCRAP_INSPECTION_BLUEPRINT[key]}
                          compact
                        />
                      ))}
                    </>
                  )}
                </div>
              </div>
            </section>

            <div className="es-split">
              <section className="es-section">
                <div className="es-section-title">첨부물</div>
                <div className="es-section-body">
                  <Controller
                    name="confirmationMethod"
                    control={control}
                    render={({ field }) => (
                      <div className="es-confirm-inline">
                        {EARLY_SCRAP_ATTACHMENT_BLUEPRINT.confirmationMethod.valueItems.map(
                          (option) => (
                            <div
                              key={option.value}
                              className="early-scrap-confirm-option"
                            >
                              <div className="early-scrap-radio-item">
                                <RadioButton
                                  inputId={`confirmation-${option.value}`}
                                  name={field.name}
                                  value={option.value}
                                  onChange={(e) => field.onChange(e.value)}
                                  checked={field.value === option.value}
                                />
                                <label htmlFor={`confirmation-${option.value}`}>
                                  {option.label}
                                </label>
                              </div>
                              <p className="early-scrap-confirm-desc">
                                {option.description}
                              </p>
                            </div>
                          )
                        )}
                      </div>
                    )}
                  />
                  {isOnlineConfirmation ? (
                    <p className="early-scrap-online-guide">
                      온라인 대상차량확인 시스템(
                      <a
                        href="https://escar.or.kr"
                        target="_blank"
                        rel="noreferrer"
                      >
                        escar.or.kr
                      </a>
                      )에 등록된 동영상으로 갈음합니다. 현장 사진 첨부는 생략할
                      수 있습니다.
                    </p>
                  ) : (
                    <div className="early-scrap-photo-grid">
                      {EARLY_SCRAP_PHOTO_BLUEPRINT.positions.map(
                        ({ position, htmlLabel }) => {
                          const preview = photoPreviews[position];
                          return (
                            <div
                              key={position}
                              className="early-scrap-photo-box"
                            >
                              {!preview && (
                                <>
                                  <span className="early-scrap-photo-icon">
                                    📷
                                  </span>
                                  <span className="early-scrap-photo-label">
                                    {htmlLabel}
                                  </span>
                                </>
                              )}
                              {preview && (
                                <button
                                  type="button"
                                  className="early-scrap-photo-preview-btn"
                                  onClick={() => openPhotoViewer(position)}
                                  title="사진 크게 보기"
                                >
                                  <img
                                    src={preview}
                                    alt={htmlLabel}
                                    className="early-scrap-photo-preview"
                                  />
                                  <span className="early-scrap-photo-caption">
                                    {htmlLabel}
                                  </span>
                                </button>
                              )}
                              <label
                                className={
                                  preview
                                    ? 'early-scrap-photo-change'
                                    : 'early-scrap-photo-file-hit'
                                }
                                htmlFor={`photo-${position}`}
                              >
                                {preview ? '변경' : ''}
                              </label>
                              <input
                                id={`photo-${position}`}
                                type="file"
                                accept="image/*"
                                onChange={(event) =>
                                  handlePhotoChange(position, event)
                                }
                              />
                            </div>
                          );
                        }
                      )}
                    </div>
                  )}
                </div>
              </section>

              <section className="es-section">
                <div className="es-section-title">판정</div>
                <div className="es-section-body">
                  <div className="es-fields es-fields-2">
                    <div className="es-field es-span-2">
                      <span>
                        {EARLY_SCRAP_JUDGMENT_BLUEPRINT.operationalStatus.title}
                      </span>
                      <RadioField
                        name="judgment.operationalStatus"
                        control={control}
                        options={
                          EARLY_SCRAP_JUDGMENT_BLUEPRINT.operationalStatus
                            .valueItems
                        }
                      />
                    </div>
                    <label className="es-field es-span-2">
                      <span>
                        {EARLY_SCRAP_JUDGMENT_BLUEPRINT.failReason.title}
                      </span>
                      <TextField
                        name="judgment.failReason"
                        control={control}
                        placeholder={
                          EARLY_SCRAP_JUDGMENT_BLUEPRINT.failReason.placeholder
                        }
                      />
                    </label>
                    <label className="es-field es-span-2">
                      <span>
                        {EARLY_SCRAP_JUDGMENT_BLUEPRINT.overallOpinion.title}
                      </span>
                      <Controller
                        name="judgment.overallOpinion"
                        control={control}
                        render={({ field }) => (
                          <InputTextarea
                            {...field}
                            rows={2}
                            className="w-full"
                            placeholder={
                              EARLY_SCRAP_JUDGMENT_BLUEPRINT.overallOpinion
                                .placeholder
                            }
                          />
                        )}
                      />
                    </label>
                  </div>
                </div>
              </section>
            </div>

            <div className="early-scrap-actions">
              <Button
                type="button"
                label="인쇄 / 출력하기"
                icon="pi pi-print"
                className="p-button-success"
                onClick={() => {
                  applyEarlyScrapPrintPageStyle();
                  window.print();
                }}
              />
              <Button
                type="submit"
                label={isEditMode ? '수정 저장' : '점검서 저장'}
                icon="pi pi-save"
                loading={loading}
              />
              {isEditMode && (
                <Button
                  type="button"
                  label="삭제"
                  icon="pi pi-trash"
                  className="p-button-danger"
                  loading={loading}
                  onClick={() => setShowDeleteConfirm(true)}
                />
              )}
              <Button
                type="button"
                label="닫기"
                icon="pi pi-times"
                className="p-button-secondary"
                onClick={() => window.close()}
              />
            </div>
          </form>
        </div>

        <EarlyScrapPrintDocument
          formValues={formValues}
          photoPreviews={photoPreviews}
          associationSealUrl={associationSealUrl}
        />
      </div>

      <ConfirmDialog
        visible={showDeleteConfirm}
        header="조기폐차 삭제확인"
        message="조기폐차 확인서를 삭제하시겠습니까?"
        icon="pi pi-trash"
        acceptLabel="네"
        acceptIcon="pi pi-check"
        acceptClassName="p-button-danger"
        accept={handleDelete}
        rejectLabel="아니오"
        reject={() => setShowDeleteConfirm(false)}
        onHide={() => setShowDeleteConfirm(false)}
      />
    </BlockUI>
  );
};

export default EarlyScrapRegistrationForm;
